import { Component, OnInit } from '@angular/core';
import moment from 'moment-timezone';
import { ComponentModal } from 'src/app/core/models';
import { ModalService, ViolationReportService } from 'src/app/core/services';
import { BaseModal } from 'src/app/core/utils/BaseModal';
import { ViolationReportDetailsModalComponent } from '../violation-report-details-modal/violation-report-details-modal.component';

@Component({
  selector: 'app-violation-report-by-date-modal',
  templateUrl: './violation-report-by-date-modal.component.html',
  styleUrls: ['./violation-report-by-date-modal.component.sass']
})
export class ViolationReportByDateModalComponent extends BaseModal implements OnInit {
  public loading = true;
  public initialState: any;
  public params: any = {};
  public timezone: string = null;
  public violationsByDate: any[] = [];
  public paginateByDateAdapter: any;
  public noDataFound = false;
  public hide = false;

  constructor(
    public modalService: ModalService,
    private violationReportService: ViolationReportService
  ) {
    super(modalService);
  }

  get isValid(): boolean { throw new Error('Method not implemented.'); }
  get lastState(): any { throw new Error('Method not implemented.'); }
  onAllSubmited(): Promise<any> { throw new Error('Method not implemented.'); }

  async ngOnInit() {
    this.params = this.initialState?.params || {};
    this.timezone = this.params.timezone || null;
    delete this.params.timezone;
    this.paginateByDateAdapter = {
      paginate: (p: any, op: string) => this.violationReportService.paginateByDate(p, op)
    };
    this.loading = false;
  }

  setViolationsByDate(response: any) {
    const items = (response?.data || []).map((item: any) => ({
      ...item,
      total: +item.total,
      inProcess: +item.inProcess,
      valid: +item.valid,
      invalid: +item.invalid,
      collapse: true,
      tree: null,
      loadingTree: false,
    }));
    this.violationsByDate = this.mergeSingleDayRows(items);
    this.noDataFound = this.violationsByDate.length === 0;
  }

  private mergeSingleDayRows(items: any[]): any[] {
    const singleDay = this.getSingleQueriedLocalDate();
    if (!singleDay || items.length <= 1) { return items; }

    const merged = items.reduce((acc: any, item: any) => ({
      ...acc,
      date: singleDay,
      total: acc.total + item.total,
      inProcess: acc.inProcess + item.inProcess,
      valid: acc.valid + item.valid,
      invalid: acc.invalid + item.invalid,
    }), { ...items[0], date: singleDay, total: 0, inProcess: 0, valid: 0, invalid: 0 });

    return [merged];
  }

  private getSingleQueriedLocalDate(): string {
    if (!this.timezone) { return null; }
    const original = this.params['date[between]'];
    if (!original) { return null; }
    const [origStart, origEnd] = original.replace(/^\[|\]$/g, '').split(',');
    if (!origStart || !origEnd || !moment.utc(origStart).isValid() || !moment.utc(origEnd).isValid()) { return null; }

    const startLocal = moment.utc(origStart).tz(this.timezone).format('YYYY-MM-DD');
    const endLocal = moment.utc(origEnd).tz(this.timezone).format('YYYY-MM-DD');
    return startLocal === endLocal ? startLocal : null;
  }

  async toggleDate(item: any) {
    if (item.tree) {
      item.collapse = !item.collapse;
      return;
    }
    item.loadingTree = true;
    const treeParams = this.buildTreeParams(item.date);
    item.tree = await this.violationReportService.reportViolationTree(treeParams);
    if (item.tree?.spots) {
      this.injectCollapseState(item.tree);
    }
    item.loadingTree = false;
    item.collapse = false;
  }

  injectCollapseState(tree: any): any {
    if (tree.spots) {
      tree.spots.forEach((s: any) => {
        s.collapse = true;
        if (s.lanes) {
          s.lanes.forEach((l: any) => {
            l.collapse = true;
            if (l.regulations) {
              l.regulations.forEach((r: any) => { r.collapse = true; });
            }
          });
        }
      });
    }
    return tree;
  }

  toggleCollapse(item: any) {
    item.collapse = !item.collapse;
  }

  formatQty(value: number, total: number): string {
    if (!total || !value) { return '0'; }
    return `${value}/${((value / total) * 100).toFixed(2)}%`;
  }

  getProgressWidth(value: number, total: number): string {
    if (!total || total === 0) { return '0%'; }
    return ((value / total) * 100).toFixed(2) + '%';
  }

  openDetails(date: string, situation: string = null, laneCode?: string, regulationId?: number) {
    const params: any = { ...this.params };
    params['date[between]'] = this.getDayDateBetween(date);

    if (laneCode) { params['laneCode[in]'] = `[${laneCode}]`; }
    if (regulationId) { params['regulationId[in]'] = `[${regulationId}]`; }

    if (situation === 'inProcess') {
      delete params['status[in]'];
      delete params['step[in]'];
      params['step[notIn]'] = '[done,lotAttribution]';
    } else if (situation === 'valid' || situation === 'invalid') {
      delete params['step[notIn]'];
      params['status[in]'] = `[${situation}]`;
      params['step[in]'] = '[lotAttribution,done]';
    }

    this.hide = true;
    this.modalService.show(new ComponentModal(ViolationReportDetailsModalComponent, { params }))
      .then(() => { }).catch(() => { this.hide = false; });
  }

  private buildTreeParams(date: string): any {
    const treeParams: any = {
      contractId: this.params.contractId,
      'date[between]': this.getDayDateBetween(date),
    };
    const preserve = ['laneCode[in]', 'regulationId[in]', 'status[in]', 'step[in]', 'step[notIn]'];
    for (const key of preserve) {
      if (this.params[key]) { treeParams[key] = this.params[key]; }
    }
    return treeParams;
  }

  private getDayDateBetween(date: string): string {
    let start = this.timezone
      ? moment.tz(`${date} 00:00:00`, this.timezone).utc()
      : moment.utc(`${date} 00:00:00`);
    let end = this.timezone
      ? moment.tz(`${date} 23:59:59`, this.timezone).utc()
      : moment.utc(`${date} 23:59:59`);

    const original = this.params['date[between]'];
    if (original) {
      const [origStart, origEnd] = original.replace(/^\[|\]$/g, '').split(',');
      if (origStart && moment.utc(origStart).isValid()) { start = moment.max(start, moment.utc(origStart)); }
      if (origEnd && moment.utc(origEnd).isValid()) { end = moment.min(end, moment.utc(origEnd)); }
    }

    return `[${start.format('YYYY-MM-DD HH:mm:ss')},${end.format('YYYY-MM-DD HH:mm:ss')}]`;
  }
}
